import { useState, useEffect, useRef } from 'react';
import '../styles/Placementdb.css';
import '../styles/FilterStyles.css';
import '../styles/Advanced.css'
import ProfileAvatarpp from '.././components/common/Avatar/ProfileAvatarpp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';

// Components
import ColumnSelectionModal from '../components/Staffrecord/ColumnSelectionModal';
import PlacedTable from '../components/Placementdb/PlacedTable';
import Pagination from '../components/Placeddb/Pagination';
import LoadingErrorState from '../components/Staffrecord/LoadingErrorState';
import { useToast } from '../components/Admissionform/ToastManager';
import UserProfileDropdown from '../components/common/Avatar/UserProfileDropdown';
import DateRangePicker from '../components/Placementdb/DateRangePicker';

// Hooks and Utils
import { useFilterPersistence } from '../utils/useFilterPersistence';
import { useStudentData, useColumnManagement } from '../utils/hooksspd';
import { downloadFilteredResumes, downloadFilteredExcel } from '../utils/excelUtilsplaced';
import { PERMANENT_COLUMNS, ALL_COLUMNS } from '../utils/placedtableConstants';

// Multi-Select Component (keep this as is)
const MultiSelectField = ({
    label,
    options = [],
    selectedValues = [],
    onChange,
    placeholder = "Select options...",
    searchPlaceholder = "Search...",
    alwaysShowOptions = [],
    disabled = false,
    keepSearchOnSelect = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isOpen]);

    const allAvailableOptions = [...new Set([
        ...options,
        ...alwaysShowOptions
    ])];

    const searchFilteredOptions = allAvailableOptions.filter(option =>
        option.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredOptions = searchFilteredOptions.sort((a, b) => {
        const aSelected = selectedValues.includes(a);
        const bSelected = selectedValues.includes(b);

        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return a.localeCompare(b);
    });

    const handleToggleOption = (option) => {
        if (disabled) return;

        const newSelected = selectedValues.includes(option)
            ? selectedValues.filter(item => item !== option)
            : [...selectedValues, option];
        onChange(newSelected);

        if (!keepSearchOnSelect) {
            setSearchTerm('');
        }
    };

    const removeTag = (optionToRemove) => {
        if (disabled) return;

        const newSelected = selectedValues.filter(item => item !== optionToRemove);
        onChange(newSelected);
    };

    return (
        <div className={`multi-select-field ${disabled ? 'disabled' : ''}`} ref={containerRef}>
            <label>{label}:</label>
            <div className="multi-select-container">
                <div className="multi-select-input-wrapper">
                    {selectedValues.length > 0 && (
                        <div className="selected-tags">
                            {selectedValues.map(value => (
                                <span key={value} className="selected-tag">
                                    {value}
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeTag(value);
                                        }}
                                        className="tag-remove"
                                        disabled={disabled}
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                    <input
                        type="text"
                        className="multi-select-search-input"
                        placeholder={selectedValues.length === 0 ? placeholder : searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onClick={() => !disabled && setIsOpen(!isOpen)}
                        onKeyDown={(e) => {
                            if (disabled) return;

                            if (e.key === 'Enter') {
                                e.preventDefault();
                                if (filteredOptions.length > 0) {
                                    handleToggleOption(filteredOptions[0]);
                                }
                            } else if (e.key === 'Escape') {
                                setIsOpen(false);
                                setSearchTerm('');
                            }
                        }}
                        disabled={disabled}
                    />
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        className={`dropdown-arrow ${isOpen ? 'open' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!disabled) setIsOpen(!isOpen);
                        }}
                    />
                </div>

                {isOpen && !disabled && (
                    <div className="multi-select-dropdown">
                        <div className="options-list">
                            {filteredOptions.length === 0 ? (
                                <div className="no-options">No options found</div>
                            ) : (
                                filteredOptions.map(option => (
                                    <div
                                        key={option}
                                        className={`option-item ${selectedValues.includes(option) ? 'selected' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleToggleOption(option);
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedValues.includes(option)}
                                            onChange={() => { }}
                                            className="option-checkbox"
                                        />
                                        <span className="option-text">{option}</span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const PlacedStudent = () => {
    const navigate = useNavigate();
    const { showSuccess, showError } = useToast();

    // Initial filter structure
    const initialFilters = {
        selectedDepartments: [],
        selectedBranches: [],
        selectedCourses: [],
        selectedPlacedBy: [],
        placementDateFrom: '',
        placementDateTo: '',
        selectedPackageRange: '',
        placementStatusFilter: 'All',
        photoReviewStatus: '',
        videoReviewStatus: '',
        googleReviewStatus: '',
        joiningStatusFilter: [],
        searchField: 'studentName',
        searchValue: '',
        currentPage: 1,
        recordsPerPage: 10
    };

    // Use the persistence hook
    const { filters, updateFilters, resetFilters, isReady } = useFilterPersistence(initialFilters, 'placedStudentFilters');

    // ✅ NEW: Track initial load state
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const hasInitializedFilters = useRef(false);

    // Pending filters for "Apply" functionality
    const [pendingDepartments, setPendingDepartments] = useState([]);
    const [pendingBranches, setPendingBranches] = useState([]);
    const [pendingCourses, setPendingCourses] = useState([]);
    const [isApplyingFilters, setIsApplyingFilters] = useState(false);
    const [pendingPlacedBy, setPendingPlacedBy] = useState([]);
    const [pendingPlacementDateFrom, setPendingPlacementDateFrom] = useState('');
    const [pendingPlacementDateTo, setPendingPlacementDateTo] = useState('');
    const [pendingPackageRange, setPendingPackageRange] = useState('');
    const [placementOfficers, setPlacementOfficers] = useState([]);
    const [pendingPhotoReviewStatus, setPendingPhotoReviewStatus] = useState('');
    const [pendingVideoReviewStatus, setPendingVideoReviewStatus] = useState('');
    const [pendingGoogleReviewStatus, setPendingGoogleReviewStatus] = useState('');
    const [pendingJoiningStatusFilter, setPendingJoiningStatusFilter] = useState([]);  // ✅ ADD
    const [pendingPlacementStatusFilter, setPendingPlacementStatusFilter] = useState('All');


    const [selectedStudents, setSelectedStudents] = useState(new Set());
    const [debouncedSearchValue, setDebouncedSearchValue] = useState('');

    // Data states
    const [userBranches, setUserBranches] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [courses, setCourses] = useState([]);
    const lastFetchParamsRef = useRef('');

    // ✅ UPDATED: Initialize pending filters ONCE when persisted filters are loaded
    useEffect(() => {
        if (isReady && !hasInitializedFilters.current) {
            console.log('Initializing pending filters with:', filters);
            setPendingDepartments(filters.selectedDepartments || []);
            setPendingBranches(filters.selectedBranches || []);
            setPendingCourses(filters.selectedCourses || []);
            setPendingPlacedBy(filters.selectedPlacedBy || []);
            setPendingPlacementDateFrom(filters.placementDateFrom || '');
            setPendingPlacementDateTo(filters.placementDateTo || '');
            setPendingPackageRange(filters.selectedPackageRange || '');
            setPendingPlacementStatusFilter(filters.placementStatusFilter || 'All');
            setPendingPhotoReviewStatus(filters.photoReviewStatus || '');
            setPendingVideoReviewStatus(filters.videoReviewStatus || '');
            setPendingGoogleReviewStatus(filters.googleReviewStatus || '');
            setPendingJoiningStatusFilter(filters.joiningStatusFilter || []);

            hasInitializedFilters.current = true;
        }
    }, [isReady, filters]);

    // Fetch placement officers
    useEffect(() => {
        const fetchPlacementOfficers = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${process.env.REACT_APP_API_URL}/placement-officer`, {
                    method: 'GET',
                    headers: {
                        ...(token && { 'Authorization': `Bearer ${token}` }),
                        'Content-Type': 'application/json'
                    }
                });

                if (response.ok) {
                    const result = await response.json();
                    if (Array.isArray(result)) {
                        setPlacementOfficers(result);
                    }
                }
            } catch (error) {
                console.error('Error fetching placement officers:', error);
                showError('Error loading placement officers.');
            }
        };

        fetchPlacementOfficers();
    }, []);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchValue(filters.searchValue || '');
        }, 1000);
        return () => clearTimeout(timer);
    }, [filters.searchValue]);

    // Custom hooks
    const {
        data,
        loading,
        error,
        pagination,
        fetchStudentRecords,
        updateStudentRecord,
        changePage,
        changeItemsPerPage
    } = useStudentData();

    const {
        selectedColumns,
        tempSelectedColumns,
        selectAll,
        isModalOpen,
        toggleSelectAll,
        toggleColumnSelection,
        confirmColumnSelection,
        cancelColumnSelection,
        openColumnModal,
        closeColumnModal
    } = useColumnManagement(PERMANENT_COLUMNS, ALL_COLUMNS);

    const searchableColumns = ALL_COLUMNS.filter(column =>
        selectedColumns.includes(column.key)
    );

    // Fetch departments data
    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${process.env.REACT_APP_API_URL}/alltech`, {
                    method: 'GET',
                    headers: {
                        ...(token && { 'Authorization': `Bearer ${token}` }),
                        'Content-Type': 'application/json'
                    }
                });

                if (response.ok) {
                    const result = await response.json();
                    if (result.success && result.data && Array.isArray(result.data)) {
                        const uniqueDepartments = [...new Set(result.data.map(item => item.Department))].filter(Boolean);
                        setDepartments(uniqueDepartments);
                    }
                }
            } catch (error) {
                console.error('Error fetching departments:', error);
                showError('Error loading departments.');
            }
        };

        fetchDepartments();
    }, []);

    // Update available courses when pending departments change
    useEffect(() => {
        const fetchCoursesByDepartment = async () => {
            if (pendingDepartments.length > 0 && pendingDepartments[0]) {
                try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(
                        `${process.env.REACT_APP_API_URL}/course-by-department?Department=${encodeURIComponent(pendingDepartments[0])}`,
                        {
                            method: 'GET',
                            headers: {
                                ...(token && { 'Authorization': `Bearer ${token}` }),
                                'Content-Type': 'application/json'
                            }
                        }
                    );

                    if (response.ok) {
                        const result = await response.json();
                        if (result.success) {
                            setCourses(result.courses || []);
                        }
                    }
                } catch (error) {
                    console.error('Error fetching courses:', error);
                    showError('Error loading courses.');
                }
            } else {
                setCourses([]);
                setPendingCourses([]);
            }
        };

        fetchCoursesByDepartment();
    }, [pendingDepartments]);

    // Fetch all branches from location API
    useEffect(() => {
        const fetchAllBranches = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${process.env.REACT_APP_API_URL}/location`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token && { Authorization: `Bearer ${token}` }),
                    },
                });

                if (response.ok) {
                    const locationData = await response.json();
                    if (Array.isArray(locationData)) {
                        const uniqueBranches = [...new Set(
                            locationData
                                .filter(item => item.status === true)
                                .map(item => item.branch)
                        )];
                        setUserBranches(uniqueBranches);
                    }
                }
            } catch (error) {
                console.error('Error fetching branches:', error);
                showError('Error loading branches.');
            }
        };

        fetchAllBranches();
    }, []);

    // ✅ CRITICAL FIX: Main data fetching useEffect
    useEffect(() => {
        // Don't fetch until filters are ready
        if (!isReady) {
            console.log('Filters not ready, skipping fetch');
            return;
        }

        const currentParams = JSON.stringify({
            page: filters.currentPage,
            limit: filters.recordsPerPage,
            search: debouncedSearchValue,
            dept: filters.selectedDepartments,
            courses: filters.selectedCourses,
            branches: filters.selectedBranches,
            placedBy: filters.selectedPlacedBy,
            placementDateFrom: filters.placementDateFrom,
            placementDateTo: filters.placementDateTo,
            placementStatusFilter: filters.placementStatusFilter,
            packageRange: filters.selectedPackageRange,
            photoReview: filters.photoReviewStatus,
            videoReview: filters.videoReviewStatus,
            googleReview: filters.googleReviewStatus,
            joiningStatus: filters.joiningStatusFilter
        });

        // Skip if params haven't changed
        if (currentParams === lastFetchParamsRef.current) {
            console.log('Skipping fetch - params unchanged');
            return;
        }

        let isCancelled = false;

        const fetchData = async () => {
            const searchParams = {
                page: filters.currentPage || 1,
                limit: filters.recordsPerPage || 10,
            };

            if (debouncedSearchValue) {
                searchParams.searchField = filters.searchField;
                searchParams.searchValue = debouncedSearchValue;
            }

            if (filters.selectedDepartments?.length > 0) {
                searchParams.departments = filters.selectedDepartments.join(',');
            }
            if (filters.selectedCourses?.length > 0) {
                searchParams.courses = filters.selectedCourses.join(',');
            }
            if (filters.selectedBranches?.length > 0) {
                searchParams.branches = filters.selectedBranches.join(',');
            }
            if (filters.selectedPlacedBy?.length > 0) {
                searchParams.placedBy = filters.selectedPlacedBy.join(',');
            }
            if (filters.placementDateFrom) {
                searchParams.placementDateFrom = filters.placementDateFrom;
            }
            if (filters.placementDateTo) {
                searchParams.placementDateTo = filters.placementDateTo;
            }
            if (filters.placementStatusFilter) {
                searchParams.placementStatusFilter = filters.placementStatusFilter;
            }
            if (filters.selectedPackageRange) {
                searchParams.packageRange = filters.selectedPackageRange;
            }
            if (filters.photoReviewStatus) {
                searchParams.photoReview = filters.photoReviewStatus;
            }
            if (filters.videoReviewStatus) {
                searchParams.videoReview = filters.videoReviewStatus;
            }
            if (filters.googleReviewStatus) {
                searchParams.googleReview = filters.googleReviewStatus;
            }
            if (filters.joiningStatusFilter?.length > 0) {
                searchParams.joiningStatus = filters.joiningStatusFilter.join(',');  // ✅ ADD
            }

            console.log('Fetching with params:', searchParams);

            if (!isCancelled) {
                lastFetchParamsRef.current = currentParams;
                await fetchStudentRecords(searchParams);

                // ✅ Mark initial load as complete after first successful fetch
                if (isInitialLoad) {
                    setIsInitialLoad(false);
                }
            }
        };

        fetchData();

        return () => {
            isCancelled = true;
        };

    }, [
        isReady,
        debouncedSearchValue,
        filters.searchField,
        filters.currentPage,
        filters.recordsPerPage,
        filters.selectedDepartments?.join(','),
        filters.selectedCourses?.join(','),
        filters.selectedBranches?.join(','),
        filters.selectedPlacedBy?.join(','),
        filters.placementDateFrom,
        filters.placementDateTo,
        filters.placementStatusFilter,
        filters.selectedPackageRange,
        filters.photoReviewStatus,
        filters.videoReviewStatus,
        filters.googleReviewStatus,
        filters.joiningStatusFilter?.join(','),
        fetchStudentRecords,
        isInitialLoad
    ]);

    // Apply filters
    const handleApplyFilters = async () => {
        setIsApplyingFilters(true);

        try {
            updateFilters({
                selectedDepartments: [...pendingDepartments],
                selectedBranches: [...pendingBranches],
                selectedCourses: [...pendingCourses],
                selectedPlacedBy: [...pendingPlacedBy],
                placementDateFrom: pendingPlacementDateFrom,
                placementDateTo: pendingPlacementDateTo,
                selectedPackageRange: pendingPackageRange,
                placementStatusFilter: pendingPlacementStatusFilter,
                photoReviewStatus: pendingPhotoReviewStatus,
                videoReviewStatus: pendingVideoReviewStatus,
                googleReviewStatus: pendingGoogleReviewStatus,
                joiningStatusFilter: pendingJoiningStatusFilter,
                currentPage: 1
            });

            showSuccess('Filters applied successfully!');
        } catch (error) {
            console.error('Error applying filters:', error);
            showError('Failed to apply filters. Please try again.');
        } finally {
            setIsApplyingFilters(false);
        }
    };

    // Check for pending changes
    const hasPendingChanges = () => {
        return (
            JSON.stringify(pendingDepartments.sort()) !== JSON.stringify((filters.selectedDepartments || []).sort()) ||
            JSON.stringify(pendingBranches.sort()) !== JSON.stringify((filters.selectedBranches || []).sort()) ||
            JSON.stringify(pendingCourses.sort()) !== JSON.stringify((filters.selectedCourses || []).sort()) ||
            JSON.stringify(pendingPlacedBy.sort()) !== JSON.stringify((filters.selectedPlacedBy || []).sort()) ||
            pendingPlacementDateFrom !== (filters.placementDateFrom || '') ||
            pendingPlacementDateTo !== (filters.placementDateTo || '') ||
            pendingPackageRange !== (filters.selectedPackageRange || '') ||
            pendingPlacementStatusFilter !== (filters.placementStatusFilter || 'Placed') ||
            pendingPhotoReviewStatus !== (filters.photoReviewStatus || '') ||
            pendingVideoReviewStatus !== (filters.videoReviewStatus || '') ||
            pendingGoogleReviewStatus !== (filters.googleReviewStatus || '') ||
            JSON.stringify(pendingJoiningStatusFilter.sort()) !== JSON.stringify((filters.joiningStatusFilter || []).sort()));
    };

    // Clear all filters
    const clearAllFilters = () => {
        setPendingDepartments([]);
        setPendingBranches([]);
        setPendingCourses([]);
        setPendingPlacedBy([]);
        setPendingPlacementDateFrom('');
        setPendingPlacementDateTo('');
        setPendingPlacementStatusFilter('All');
        setPendingPackageRange('');
        setPendingPhotoReviewStatus('');
        setPendingVideoReviewStatus('');
        setPendingGoogleReviewStatus('');
        setPendingJoiningStatusFilter([]);
        resetFilters();
    };

    // Page change handler
    const handlePageChange = async (page) => {
        if (page >= 1 && page <= pagination.totalPages && page !== pagination.currentPage) {
            const currentFilters = {
                searchField: filters.searchValue ? filters.searchField : undefined,
                searchValue: filters.searchValue || undefined,
                departments: filters.selectedDepartments?.length > 0 ? filters.selectedDepartments.join(',') : undefined,
                courses: filters.selectedCourses?.length > 0 ? filters.selectedCourses.join(',') : undefined,
                branches: filters.selectedBranches?.length > 0 ? filters.selectedBranches.join(',') : undefined,
                placedBy: filters.selectedPlacedBy?.length > 0 ? filters.selectedPlacedBy.join(',') : undefined,
                placementDateFrom: filters.placementDateFrom || undefined,
                placementDateTo: filters.placementDateTo || undefined,
                packageRange: filters.selectedPackageRange || undefined,
                placementStatusFilter: filters.placementStatusFilter || undefined,
                photoReview: filters.photoReviewStatus || undefined,
                videoReview: filters.videoReviewStatus || undefined,
                googleReview: filters.googleReviewStatus || undefined,
                joiningStatus: filters.joiningStatusFilter?.length > 0 ? filters.joiningStatusFilter.join(',') : undefined,
            };

            Object.keys(currentFilters).forEach(key =>
                currentFilters[key] === undefined && delete currentFilters[key]
            );

            await changePage(page, currentFilters);
            updateFilters({ currentPage: page });
        }
    };

    const handleItemsPerPageChange = async (newLimit) => {
        const currentFilters = {
            searchField: filters.searchValue ? filters.searchField : undefined,
            searchValue: filters.searchValue || undefined,
            departments: filters.selectedDepartments?.length > 0 ? filters.selectedDepartments.join(',') : undefined,
            courses: filters.selectedCourses?.length > 0 ? filters.selectedCourses.join(',') : undefined,
            branches: filters.selectedBranches?.length > 0 ? filters.selectedBranches.join(',') : undefined,
            placedBy: filters.selectedPlacedBy?.length > 0 ? filters.selectedPlacedBy.join(',') : undefined,
            placementDateFrom: filters.placementDateFrom || undefined,
            placementDateTo: filters.placementDateTo || undefined,
            placementStatusFilter: filters.placementStatusFilter || undefined,
            packageRange: filters.selectedPackageRange || undefined,
            photoReview: filters.photoReviewStatus || undefined,
            videoReview: filters.videoReviewStatus || undefined,
            googleReview: filters.googleReviewStatus || undefined,
            joiningStatus: filters.joiningStatusFilter?.length > 0 ? filters.joiningStatusFilter.join(',') : undefined,  // ✅ ADD

        };

        Object.keys(currentFilters).forEach(key =>
            currentFilters[key] === undefined && delete currentFilters[key]
        );

        await changeItemsPerPage(newLimit, currentFilters);
        updateFilters({ recordsPerPage: newLimit, currentPage: 1 });
    };

    const handleDownloadExcel = async () => {
        const currentFilters = {
            searchField: filters.searchValue ? filters.searchField : undefined,
            searchValue: filters.searchValue || undefined,
            departments: filters.selectedDepartments?.length > 0 ? filters.selectedDepartments.join(',') : undefined,
            courses: filters.selectedCourses?.length > 0 ? filters.selectedCourses.join(',') : undefined,
            branches: filters.selectedBranches?.length > 0 ? filters.selectedBranches.join(',') : undefined,
            placedBy: filters.selectedPlacedBy?.length > 0 ? filters.selectedPlacedBy.join(',') : undefined,
            placementStatusFilter: filters.placementStatusFilter || undefined,
            placementDateFrom: filters.placementDateFrom || undefined,
            placementDateTo: filters.placementDateTo || undefined,
            packageRange: filters.selectedPackageRange || undefined,
            photoReview: filters.photoReviewStatus || undefined,
            videoReview: filters.videoReviewStatus || undefined,
            googleReview: filters.googleReviewStatus || undefined,
            joiningStatus: filters.joiningStatusFilter?.length > 0 ? filters.joiningStatusFilter.join(',') : undefined,  // ✅ ADD
        };

        Object.keys(currentFilters).forEach(key => {
            if (currentFilters[key] === undefined) {
                delete currentFilters[key];
            }
        });

        await downloadFilteredExcel(currentFilters, showSuccess, showError);
    };

    const handleStudentSelection = (studentId, isSelected) => {
        const newSelectedStudents = new Set(selectedStudents);
        if (isSelected) {
            newSelectedStudents.add(studentId);
        } else {
            newSelectedStudents.delete(studentId);
        }
        setSelectedStudents(newSelectedStudents);
    };

    const handleSelectAll = (isSelected) => {
        if (isSelected) {
            const currentPageStudentIds = data.map(student => student.studentId);
            const newSelectedStudents = new Set([...selectedStudents, ...currentPageStudentIds]);
            setSelectedStudents(newSelectedStudents);
        } else {
            const currentPageStudentIds = new Set(data.map(student => student.studentId));
            const newSelectedStudents = new Set([...selectedStudents].filter(id => !currentPageStudentIds.has(id)));
            setSelectedStudents(newSelectedStudents);
        }
    };

    const handleBulkDownload = async () => {
        if (selectedStudents.size === 0) {
            showError('Please select at least one student to download resumes.');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/resumes/download-bulk`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    studentIds: Array.from(selectedStudents)
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to download resumes');
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const currentDate = new Date().toISOString().split('T')[0];
            link.download = `Selected_Resumes_${currentDate}.zip`;

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            showSuccess(`Successfully downloaded ${selectedStudents.size} resume(s)!`);
            setSelectedStudents(new Set());

        } catch (error) {
            console.error('Error downloading bulk resumes:', error);
            showError(`Failed to download resumes: ${error.message}`);
        }
    };

    const handleDownload = async () => {
        const currentFilters = {
            searchField: filters.searchValue ? filters.searchField : undefined,
            searchValue: filters.searchValue || undefined,
            departments: filters.selectedDepartments?.length > 0 ? filters.selectedDepartments.join(',') : undefined,
            courses: filters.selectedCourses?.length > 0 ? filters.selectedCourses.join(',') : undefined,
            branches: filters.selectedBranches?.length > 0 ? filters.selectedBranches.join(',') : undefined,
            placedBy: filters.selectedPlacedBy?.length > 0 ? filters.selectedPlacedBy.join(',') : undefined,
            placementStatusFilter: filters.placementStatusFilter || undefined,
            placementDateFrom: filters.placementDateFrom || undefined,
            placementDateTo: filters.placementDateTo || undefined,
            packageRange: filters.selectedPackageRange || undefined,
            photoReview: filters.photoReviewStatus || undefined,
            videoReview: filters.videoReviewStatus || undefined,
            googleReview: filters.googleReviewStatus || undefined,
            joiningStatus: filters.joiningStatusFilter?.length > 0 ? filters.joiningStatusFilter.join(',') : undefined,  // ✅ ADD
        };

        Object.keys(currentFilters).forEach(key => {
            if (currentFilters[key] === undefined) {
                delete currentFilters[key];
            }
        });

        await downloadFilteredResumes(currentFilters, showSuccess, showError);
    };

    const handleEdit = (rowIndex) => {
        const rowData = { ...data[rowIndex] };
        console.log('Navigating to edit with current filters:', filters);
        navigate(`/edit-student/${rowData.studentId}`);
    };



    const handleStaffUpdate = async (displayRowIndex, staffData) => {
        try {
            if (!data[displayRowIndex]) {
                throw new Error('Invalid record index');
            }

            const currentRecord = { ...data[displayRowIndex] };
            const updatedRecord = {
                ...currentRecord,
                staffId2: staffData.staffId2 || '',
                staffName2: staffData.staffName2 || '',
                staffId3: staffData.staffId3 || '',
                staffName3: staffData.staffName3 || ''
            };

            await updateStudentRecord(updatedRecord);

            const currentFilters = {
                searchField: filters.searchValue ? filters.searchField : undefined,
                searchValue: filters.searchValue || undefined,
                departments: filters.selectedDepartments?.length > 0 ? filters.selectedDepartments.join(',') : undefined,
                courses: filters.selectedCourses?.length > 0 ? filters.selectedCourses.join(',') : undefined,
                branches: filters.selectedBranches?.length > 0 ? filters.selectedBranches.join(',') : undefined
            };

            Object.keys(currentFilters).forEach(key =>
                currentFilters[key] === undefined && delete currentFilters[key]
            );

            await fetchStudentRecords({
                page: filters.currentPage || 1,
                limit: filters.recordsPerPage || 10,
                ...currentFilters
            });

            showSuccess('Staff details updated successfully!');
        } catch (error) {
            console.error('Staff update error:', error);
            showError('Failed to update staff details. Please try again.');
        }
    };

    // ✅ Show loading state during initial load
    const loadingErrorComponent = LoadingErrorState({
        loading: loading || isInitialLoad,
        error,
        onRetry: () => fetchStudentRecords({
            page: filters.currentPage || 1,
            limit: filters.recordsPerPage || 10
        })
    });

    if (loadingErrorComponent) {
        return loadingErrorComponent;
    }

    const hasActiveFilters =
        filters.selectedDepartments?.length > 0 ||
        filters.selectedBranches?.length > 0 ||
        filters.selectedCourses?.length > 0 ||
        filters.selectedPlacedBy?.length > 0 ||
        (filters.placementDateFrom && filters.placementDateFrom !== "") ||
        (filters.placementDateTo && filters.placementDateTo !== "") ||
        (filters.placementStatusFilter && filters.placementStatusFilter !== 'All') ||
        (filters.selectedPackageRange && filters.selectedPackageRange !== "") ||
        (filters.photoReviewStatus && filters.photoReviewStatus !== "") ||
        (filters.videoReviewStatus && filters.videoReviewStatus !== "") ||
        (filters.googleReviewStatus && filters.googleReviewStatus !== "") ||
        (filters.joiningStatusFilter?.length > 0);


    return (
        <div className="dashboard">
            <div className="dashboard-header">
                <h1 className="dashboard-heading">PLACED STUDENT DETAILS</h1>
                <div className="flex items-center gap-4">
                    <UserProfileDropdown />
                    <ProfileAvatarpp />
                </div>
            </div>

            <div className="record-count-wrapper">
                <div className="record-count-card">
                    <div className="card-content">
                        <div className="text-section">
                            <span className="label">STUDENT RECORDS COUNT</span>
                            <h2 className="value">
                                Viewing {pagination.recordsOnCurrentPage || data.length} of {pagination.totalRecords} records
                            </h2>
                        </div>
                        <div className="icon-section">
                            <FontAwesomeIcon icon={faUsers} />
                        </div>
                    </div>
                </div>
            </div>

            <div className='head'>
                <h3>Search Student Records</h3>
            </div>

            {/* Filter Summary */}
            <div className="filter-summary-row">
                {hasActiveFilters && (
                    <div className="active-filters-summary">
                        <span className="filters-label">Active Filters:</span>

                        {filters.selectedDepartments?.map((dept, index) => (
                            <span key={`department-${index}`} className="filter-badge department">
                                {dept}
                                <button
                                    className="clear-filter-btn"
                                    onClick={() => {
                                        const updated = filters.selectedDepartments.filter(d => d !== dept);
                                        updateFilters({ selectedDepartments: updated });
                                        setPendingDepartments(updated);
                                    }}

                                >
                                    ×
                                </button>
                            </span>
                        ))}

                        {filters.selectedCourses?.map((course, index) => (
                            <span key={`course-${index}`} className="filter-badge course">
                                {course}
                                <button
                                    className="clear-filter-btn"
                                    onClick={() => {
                                        const updated = filters.selectedCourses.filter(c => c !== course);
                                        updateFilters({ selectedCourses: updated });
                                        setPendingCourses(updated);
                                    }}

                                >
                                    ×
                                </button>
                            </span>
                        ))}

                        {filters.selectedBranches?.map((branch, index) => (
                            <span key={`branch-${index}`} className="filter-badge branch">
                                {branch}
                                <button
                                    className="clear-filter-btn"
                                    onClick={() => {
                                        const updated = filters.selectedBranches.filter(b => b !== branch);
                                        updateFilters({ selectedBranches: updated });
                                        setPendingBranches(updated);
                                    }}

                                >
                                    ×
                                </button>
                            </span>
                        ))}
                        {filters.selectedPlacedBy?.map((officer, index) => (
                            <span key={`placedby-${index}`} className="filter-badge placedby">
                                {officer}
                                <button
                                    className="clear-filter-btn"
                                    onClick={() => {
                                        const updated = filters.selectedPlacedBy.filter(o => o !== officer);
                                        updateFilters({ selectedPlacedBy: updated });
                                        setPendingPlacedBy(updated);
                                    }}

                                >
                                    ×
                                </button>
                            </span>
                        ))}

                        {filters.placementDateFrom && (
                            <span className="filter-badge date">
                                From: {filters.placementDateFrom}
                                <button
                                    className="clear-filter-btn"
                                    onClick={() => {
                                        updateFilters({ placementDateFrom: '' });
                                        setPendingPlacementDateFrom('');
                                    }}
                                >
                                    ×
                                </button>
                            </span>
                        )}

                        {filters.placementDateTo && (
                            <span className="filter-badge date">
                                To: {filters.placementDateTo}
                                <button
                                    className="clear-filter-btn"
                                    onClick={() => {
                                        updateFilters({ placementDateTo: '' });
                                        setPendingPlacementDateTo('');
                                    }}
                                >
                                    ×
                                </button>
                            </span>
                        )}

                        {filters.selectedPackageRange && (
                            <span className="filter-badge package">
                                Package: {filters.selectedPackageRange}
                                <button
                                    className="clear-filter-btn"
                                    onClick={() => {
                                        updateFilters({ selectedPackageRange: '' });
                                        setPendingPackageRange('');
                                    }}
                                >
                                    ×
                                </button>
                            </span>
                        )}
                        {filters.placementStatusFilter && filters.placementStatusFilter !== 'All' && (
                            <span className="filter-badge status">
                                Status: {filters.placementStatusFilter}
                                <button
                                    className="clear-filter-btn"
                                    onClick={() => {
                                        updateFilters({ placementStatusFilter: 'Placed' });
                                        setPendingPlacementStatusFilter('Placed');
                                    }}
                                >
                                    ×
                                </button>
                            </span>
                        )}
                        {filters.photoReviewStatus && (
                            <span className="filter-badge review">
                                Photo: {filters.photoReviewStatus === 'not_filled' ? 'Not Filled' : filters.photoReviewStatus}
                                <button
                                    className="clear-filter-btn"
                                    onClick={() => {
                                        updateFilters({ photoReviewStatus: '' });
                                        setPendingPhotoReviewStatus('');
                                    }}
                                >
                                    ×
                                </button>
                            </span>
                        )}

                        {filters.videoReviewStatus && (
                            <span className="filter-badge review">
                                Video: {filters.videoReviewStatus === 'not_filled' ? 'Not Filled' : filters.videoReviewStatus}
                                <button
                                    className="clear-filter-btn"
                                    onClick={() => {
                                        updateFilters({ videoReviewStatus: '' });
                                        setPendingVideoReviewStatus('');
                                    }}
                                >
                                    ×
                                </button>
                            </span>
                        )}

                        {filters.googleReviewStatus && (
                            <span className="filter-badge review">
                                Google: {filters.googleReviewStatus === 'not_filled' ? 'Not Filled' : filters.googleReviewStatus}
                                <button
                                    className="clear-filter-btn"
                                    onClick={() => {
                                        updateFilters({ googleReviewStatus: '' });
                                        setPendingGoogleReviewStatus('');
                                    }}
                                >
                                    ×
                                </button>
                            </span>
                        )}
                        {filters.joiningStatusFilter?.map((status, index) => (
                            <span key={`joiningstatus-${index}`} className="filter-badge status">
                                {status}
                                <button
                                    className="clear-filter-btn"
                                    onClick={() => {
                                        const updated = filters.joiningStatusFilter.filter(s => s !== status);
                                        updateFilters({ joiningStatusFilter: updated });
                                        setPendingJoiningStatusFilter(updated);
                                    }}
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                        <button onClick={clearAllFilters} className="clear-all-btn">
                            Clear All
                        </button>
                    </div>
                )}
            </div>

            <div className="advanced-filters-panel">
                <div className="filters-grid">
                    <MultiSelectField
                        label="Branch"
                        options={userBranches}
                        selectedValues={pendingBranches}
                        onChange={setPendingBranches}
                        placeholder="Select branches..."
                        searchPlaceholder="Search branches..."
                    />

                    <div className="multi-select-field">
                        <label>Department:</label>
                        <div className="multi-select-container">
                            <select
                                className="multi-select-input-wrapper"
                                style={{ width: '100%', cursor: 'pointer' }}
                                value={pendingDepartments[0] || ""}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setPendingDepartments(value ? [value] : []);
                                    setPendingCourses([]);
                                }}
                            >
                                <option value="">Select department...</option>
                                {departments.map((dept) => (
                                    <option key={dept} value={dept}>
                                        {dept}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <MultiSelectField
                        label="Course Name"
                        options={courses}
                        selectedValues={pendingCourses}
                        onChange={setPendingCourses}
                        placeholder="Select courses..."
                        searchPlaceholder="Search courses..."
                        disabled={pendingDepartments.length === 0}
                        keepSearchOnSelect={true}
                    />
                    <MultiSelectField
                        label="Placed By"
                        options={['Self (Student)', 'Others', ...placementOfficers]}
                        selectedValues={pendingPlacedBy}
                        onChange={setPendingPlacedBy}
                        placeholder="Select placement officers..."
                        searchPlaceholder="Search officers..."
                        keepSearchOnSelect={true}
                    />

                    <div className="multi-select-field">
                        <label>Package Range:</label>
                        <div className="multi-select-container">
                            <select
                                className="multi-select-input-wrapper"
                                style={{ width: '100%', cursor: 'pointer' }}
                                value={pendingPackageRange}
                                onChange={(e) => setPendingPackageRange(e.target.value)}
                            >
                                <option value="">Select package range...</option>
                                <option value="<2">{"< 2 LPA"}</option>
                                <option value="2-4">2 - 4 LPA</option>
                                <option value="4-6">4 - 6 LPA</option>
                                <option value=">6">{"> 6 LPA"}</option>
                            </select>
                        </div>
                    </div>

                    <DateRangePicker
                        dateFrom={pendingPlacementDateFrom}
                        dateTo={pendingPlacementDateTo}
                        onDateFromChange={setPendingPlacementDateFrom}
                        onDateToChange={setPendingPlacementDateTo}
                        label="Placement Date Range"
                    />
                    <div className="multi-select-field">
                        <label>Placement Status:</label>
                        <div className="multi-select-container">
                            <select
                                className="multi-select-input-wrapper"
                                style={{ width: '100%', cursor: 'pointer' }}
                                value={pendingPlacementStatusFilter}
                                onChange={(e) => setPendingPlacementStatusFilter(e.target.value)}
                            >
                                <option value="All">All</option>
                                <option value="Placed">Placed</option>
                                <option value="Placed but not accepted the Offer">Placed but not accepted the Offer</option>
                            </select>
                        </div>
                    </div>

                    <div className="multi-select-field">
                        <label>Photo Review:</label>
                        <div className="multi-select-container">
                            <select
                                className="multi-select-input-wrapper"
                                style={{ width: '100%', cursor: 'pointer' }}
                                value={pendingPhotoReviewStatus}
                                onChange={(e) => setPendingPhotoReviewStatus(e.target.value)}
                            >
                                <option value="">All</option>
                                <option value="Yes">Yes</option>
                                <option value="No">Not Possible to get the review</option>
                                <option value="not_filled">Not Filled</option>
                            </select>
                        </div>
                    </div>

                    <div className="multi-select-field">
                        <label>Video Review:</label>
                        <div className="multi-select-container">
                            <select
                                className="multi-select-input-wrapper"
                                style={{ width: '100%', cursor: 'pointer' }}
                                value={pendingVideoReviewStatus}
                                onChange={(e) => setPendingVideoReviewStatus(e.target.value)}
                            >
                                <option value="">All</option>
                                <option value="Yes">Yes</option>
                                <option value="No">Not Possible to get the review</option>
                                <option value="not_filled">Not Filled</option>
                            </select>
                        </div>
                    </div>

                    <div className="multi-select-field">
                        <label>Google Review:</label>
                        <div className="multi-select-container">
                            <select
                                className="multi-select-input-wrapper"
                                style={{ width: '100%', cursor: 'pointer' }}
                                value={pendingGoogleReviewStatus}
                                onChange={(e) => setPendingGoogleReviewStatus(e.target.value)}
                            >
                                <option value="">All</option>
                                <option value="Yes">Yes</option>
                                <option value="No">Not Possible to get the review</option>
                                <option value="not_filled">Not Filled</option>
                            </select>
                        </div>
                    </div>
                    <MultiSelectField
                        label="Joining Status"
                        options={['Verified', 'Incorrect Data', 'Not Responding', 'Not Updated']}
                        selectedValues={pendingJoiningStatusFilter}
                        onChange={setPendingJoiningStatusFilter}
                        placeholder="Select joining status..."
                        searchPlaceholder="Search status..."
                        alwaysShowOptions={['Verified', 'Incorrect Data', 'Not Responding', 'Not Updated']}
                    />
                </div>

                <div className="filters-actions">
                    <button onClick={clearAllFilters} className="clear-btn">
                        Clear All Filters
                    </button>

                    <button
                        onClick={handleApplyFilters}
                        className="apply-filters-btn"
                        disabled={isApplyingFilters || !hasPendingChanges()}
                    >
                        {isApplyingFilters ? (
                            <>
                                <span className="loading-spinner">⟳</span>
                                Applying...
                            </>
                        ) : (
                            <>
                                <span>✓</span>
                                Apply Filters
                            </>
                        )}
                    </button>
                </div>
            </div>

            <div className="search-bar-container">
                <div className="searchsr-bar">
                    <select
                        className="select-dropdown"
                        onChange={(e) => updateFilters({ searchField: e.target.value })}
                        value={filters.searchField || ""}
                    >
                        {searchableColumns.map((column) => (
                            <option key={column.key} value={column.key}>
                                {column.label}
                            </option>
                        ))}
                    </select>

                    <input
                        type="text"
                        placeholder={`Search by ${searchableColumns.find((col) => col.key === filters.searchField)?.label || "Student Name"}...`}
                        value={filters.searchValue || ""}
                        onChange={(e) => updateFilters({ searchValue: e.target.value })}
                        className="search-input"
                        autoComplete="off"
                    />
                </div>
            </div>

            <>
                <PlacedTable
                    currentItems={data}
                    selectedColumns={selectedColumns}
                    indexOfFirstItem={0}
                    onEdit={handleEdit}
                    onStaffUpdate={handleStaffUpdate}
                    selectedStudents={selectedStudents}
                    onStudentSelection={handleStudentSelection}
                    onSelectAll={handleSelectAll}
                    onBulkDownload={handleBulkDownload}
                />

                <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    itemsPerPage={pagination.recordsPerPage}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    onOpenColumnModal={openColumnModal}
                    onDownload={handleDownload}
                    onDownloadExcel={handleDownloadExcel}
                />
            </>

            <ColumnSelectionModal
                isOpen={isModalOpen}
                tempSelectedColumns={tempSelectedColumns}
                selectAll={selectAll}
                onToggleSelectAll={toggleSelectAll}
                onToggleColumn={toggleColumnSelection}
                onConfirm={confirmColumnSelection}
                onCancel={cancelColumnSelection}
                onClose={closeColumnModal}
            />
        </div>
    );
};

export default PlacedStudent;